import React, { useState, useEffect } from 'react'
import { dbOperations } from '../../utils/supabase'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  StarIcon,
  CubeIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'

const ProductSetsPage = () => {
  const [sets, setSets] = useState([])
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [showItemsModal, setShowItemsModal] = useState(false)
  const [editingSet, setEditingSet] = useState(null)
  const [selectedSet, setSelectedSet] = useState(null)
  const [setItems, setSetItems] = useState([])
  
  // Modal tab state
  const [activeModalTab, setActiveModalTab] = useState('info')
  const [tempSetItems, setTempSetItems] = useState([])
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    set_price: '',
    discount_amount: '',
    category: '',
    image_url: '',
    is_active: true,
    is_featured: false,
    tags: ''
  })

  // Set items form
  const [selectedProduct, setSelectedProduct] = useState('')
  const [selectedQuantity, setSelectedQuantity] = useState('1')

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [setsResponse, productsResponse] = await Promise.all([
        dbOperations.productSets.getAll(),
        dbOperations.products.getAll()
      ])
      
      setSets(setsResponse.data || [])
      setProducts(productsResponse.data || [])
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadSetItems = async (setId) => {
    try {
      const { data: items } = await dbOperations.productSetItems.getBySetId(setId)
      setSetItems(items || [])
    } catch (error) {
      console.error('Error loading set items:', error)
      setSetItems([])
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Otomatik fiyat hesapla eğer manuel girilmemişse
      let finalPrice = parseFloat(formData.set_price)
      if (!finalPrice && tempSetItems.length > 0) {
        const totalPrice = tempSetItems.reduce((total, item) => {
          const product = products.find(p => p.id === item.product_id)
          return total + (product?.price || 0) * item.quantity
        }, 0)
        finalPrice = totalPrice
      }

      const setData = {
        ...formData,
        set_price: finalPrice,
        discount_amount: parseFloat(formData.discount_amount) || 0
      }

      if (editingSet) {
        // Güncelleme
        await dbOperations.productSets.update(editingSet.id, setData)
      } else {
        // Yeni takım oluştur
        const { data: newSet, error: setError } = await dbOperations.productSets.create(setData)
        
        if (setError) throw setError

        // Takım ürünlerini ekle
        for (const item of tempSetItems) {
          const product = products.find(p => p.id === item.product_id)
          await dbOperations.productSetItems.create({
            set_id: newSet.id,
            product_id: item.product_id,
            quantity: item.quantity,
            price_per_unit: product?.price || 0
          })
        }
      }

      await loadData()
      closeModal()
      alert(editingSet ? 'Takım güncellendi!' : 'Takım oluşturuldu!')
    } catch (error) {
      console.error('Error saving set:', error)
      alert('Takım kaydedilirken hata oluştu!')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (set) => {
    if (!window.confirm(`"${set.name}" takımını silmek istediğinizden emin misiniz?`)) {
      return
    }

    try {
      await dbOperations.productSets.delete(set.id)
      await loadData()
      alert('Takım silindi!')
    } catch (error) {
      console.error('Error deleting set:', error)
      alert('Takım silinirken hata oluştu!')
    }
  }

  const toggleFeatured = async (set) => {
    try {
      await dbOperations.productSets.update(set.id, {
        is_featured: !set.is_featured
      })
      await loadData()
    } catch (error) {
      console.error('Error updating featured status:', error)
      alert('Öne çıkarma durumu güncellenirken hata oluştu!')
    }
  }

  const openModal = async (set = null) => {
    setEditingSet(set)
    setActiveModalTab('info')
    setTempSetItems([])
    
    if (set) {
      setFormData({
        name: set.name || '',
        description: set.description || '',
        set_price: set.set_price || '',
        discount_amount: set.discount_amount || '',
        category: set.category || '',
        image_url: set.image_url || '',
        is_active: set.is_active,
        is_featured: set.is_featured,
        tags: set.tags || ''
      })
      
      // Mevcut takımın ürünlerini yükle
      try {
        const { data: items } = await dbOperations.productSetItems.getBySetId(set.id)
        setTempSetItems(items?.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          product_name: item.products?.name,
          product_price: item.products?.price
        })) || [])
      } catch (error) {
        console.error('Error loading set items:', error)
      }
    } else {
      setFormData({
        name: '',
        description: '',
        set_price: '',
        discount_amount: '',
        category: '',
        image_url: '',
        is_active: true,
        is_featured: false,
        tags: ''
      })
    }
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingSet(null)
    setActiveModalTab('info')
    setTempSetItems([])
  }

  const openItemsModal = async (set) => {
    setSelectedSet(set)
    await loadSetItems(set.id)
    setShowItemsModal(true)
  }

  const closeItemsModal = () => {
    setShowItemsModal(false)
    setSelectedSet(null)
    setSetItems([])
  }

  const addSetItem = async () => {
    if (!selectedProduct || !selectedSet) return

    try {
      const product = products.find(p => p.id === selectedProduct)
      await dbOperations.productSetItems.create({
        set_id: selectedSet.id,
        product_id: selectedProduct,
        quantity: parseInt(selectedQuantity),
        price_per_unit: product?.price || 0
      })

      await loadSetItems(selectedSet.id)
      setSelectedProduct('')
      setSelectedQuantity('1')
      alert('Ürün takıma eklendi!')
    } catch (error) {
      console.error('Error adding item to set:', error)
      alert('Ürün eklenirken hata oluştu!')
    }
  }

  const removeSetItem = async (itemId) => {
    if (!window.confirm('Bu ürünü takımdan çıkarmak istediğinizden emin misiniz?')) {
      return
    }

    try {
      await dbOperations.productSetItems.delete(itemId)
      await loadSetItems(selectedSet.id)
      alert('Ürün takımdan çıkarıldı!')
    } catch (error) {
      console.error('Error removing item from set:', error)
      alert('Ürün çıkarılırken hata oluştu!')
    }
  }

  const addTempSetItem = () => {
    if (!selectedProduct) return

    const existingItem = tempSetItems.find(item => item.product_id === selectedProduct)
    if (existingItem) {
      setTempSetItems(tempSetItems.map(item => 
        item.product_id === selectedProduct 
          ? { ...item, quantity: item.quantity + parseInt(selectedQuantity) }
          : item
      ))
    } else {
      const product = products.find(p => p.id === selectedProduct)
      setTempSetItems([...tempSetItems, {
        product_id: selectedProduct,
        quantity: parseInt(selectedQuantity),
        product_name: product?.name,
        product_price: product?.price
      }])
    }

    setSelectedProduct('')
    setSelectedQuantity('1')
  }

  const removeTempSetItem = (productId) => {
    setTempSetItems(tempSetItems.filter(item => item.product_id !== productId))
  }

  const calculateTotalPrice = () => {
    return tempSetItems.reduce((total, item) => {
      const product = products.find(p => p.id === item.product_id)
      return total + (product?.price || 0) * item.quantity
    }, 0)
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount)
  }

  if (loading && sets.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">🎯 Ürün Takımları</h1>
          <p className="mt-1 text-sm text-gray-600">
            Ürün takımlarını yönetin ve özelleştirin
          </p>
        </div>
        <button
          onClick={() => window.location.href = '/admin/product-sets/add'}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Yeni Takım
        </button>
      </div>

      {/* Sets Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sets.map((set) => (
          <div key={set.id} className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
            {/* Set Image */}
            <div className="h-48 bg-gray-200 relative">
              {set.image_url ? (
                <img
                  src={set.image_url}
                  alt={set.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <CubeIcon className="h-16 w-16 text-gray-400" />
                </div>
              )}
              
              {/* Featured Badge */}
              {set.is_featured && (
                <div className="absolute top-2 right-2 bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                  ⭐ Öne Çıkan
                </div>
              )}
              
              {/* Status Badge */}
              <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-medium ${
                set.is_active 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {set.is_active ? 'Aktif' : 'Pasif'}
              </div>
            </div>

            {/* Set Info */}
            <div className="p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">{set.name}</h3>
              {set.category && (
                <p className="text-sm text-gray-500 mb-2">{set.category}</p>
              )}
              <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                {set.description || 'Açıklama bulunmuyor'}
              </p>
              
              {/* Price Info */}
              <div className="mb-4">
                <p className="text-xl font-bold text-indigo-600">
                  {formatCurrency(set.set_price)}
                </p>
                {set.discount_amount > 0 && (
                  <p className="text-sm text-green-600">
                    {formatCurrency(set.discount_amount)} indirim
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between">
                <div className="flex space-x-2">
                  <button
                    onClick={() => openItemsModal(set)}
                    className="p-2 text-gray-400 hover:text-blue-600 rounded-full hover:bg-blue-50"
                    title="İçeriği Görüntüle"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </button>
                  
                  <button
                    onClick={() => openModal(set)}
                    className="p-2 text-gray-400 hover:text-green-600 rounded-full hover:bg-green-50"
                    title="Düzenle"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  
                  <button
                    onClick={() => handleDelete(set)}
                    className="p-2 text-gray-400 hover:text-red-600 rounded-full hover:bg-red-50"
                    title="Sil"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>

                <button
                  onClick={() => toggleFeatured(set)}
                  className={`p-2 rounded-full ${
                    set.is_featured
                      ? 'text-yellow-600 hover:bg-yellow-50'
                      : 'text-gray-400 hover:text-yellow-600 hover:bg-yellow-50'
                  }`}
                  title={set.is_featured ? 'Öne Çıkarmayı Kaldır' : 'Öne Çıkar'}
                >
                  {set.is_featured ? (
                    <StarIconSolid className="h-5 w-5" />
                  ) : (
                    <StarIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {sets.length === 0 && (
        <div className="text-center py-12">
          <CubeIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Takım bulunamadı</h3>
          <p className="mt-1 text-sm text-gray-500">
            İlk ürün takımınızı oluşturmak için başlayın.
          </p>
          <div className="mt-6">
            <button
              onClick={() => window.location.href = '/admin/product-sets/add'}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Yeni Takım
            </button>
          </div>
        </div>
      )}

      {/* Add/Edit Set Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingSet ? '✏️ Takım Düzenle' : '➕ Yeni Takım Oluştur'}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Tab Navigation */}
            <div className="border-b border-gray-200 mb-6">
              <nav className="-mb-px flex space-x-8">
                {[
                  { id: 'info', name: 'Takım Bilgileri', icon: '📝' },
                  { id: 'products', name: 'Ürün Seçimi', icon: '🛋️' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveModalTab(tab.id)}
                    className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                      activeModalTab === tab.id
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <span>{tab.icon}</span>
                    <span>{tab.name}</span>
                  </button>
                ))}
              </nav>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Takım Bilgileri Tab */}
              {activeModalTab === 'info' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Takım Adı *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Açıklama
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Takım Fiyatı (₺)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.set_price}
                      onChange={(e) => setFormData({ ...formData, set_price: e.target.value })}
                      placeholder="Otomatik hesaplanacak"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Boş bırakırsanız ürün fiyatları toplamı alınır
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      İndirim Tutarı (₺)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.discount_amount}
                      onChange={(e) => setFormData({ ...formData, discount_amount: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Kategori
                    </label>
                    <input
                      type="text"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Resim URL
                    </label>
                    <input
                      type="url"
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Etiketler
                    </label>
                    <input
                      type="text"
                      value={formData.tags}
                      onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                      placeholder="virgül ile ayırın"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                      Aktif
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_featured"
                      checked={formData.is_featured}
                      onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                      className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_featured" className="ml-2 block text-sm text-gray-900">
                      Öne Çıkar
                    </label>
                  </div>
                </div>
              )}

              {/* Ürün Seçimi Tab */}
              {activeModalTab === 'products' && (
                <div className="space-y-6">
                  {/* Ürün Ekleme */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                      🛋️ Takıma Ürün Ekle
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="md:col-span-2">
                        <select
                          value={selectedProduct}
                          onChange={(e) => setSelectedProduct(e.target.value)}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="">Ürün Seçin</option>
                          {products.filter(p => !tempSetItems.find(item => item.product_id === p.id)).map(product => (
                            <option key={product.id} value={product.id}>
                              {product.name} - {formatCurrency(product.price)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          min="1"
                          value={selectedQuantity}
                          onChange={(e) => setSelectedQuantity(e.target.value)}
                          placeholder="Adet"
                          className="block w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <button
                          type="button"
                          onClick={addTempSetItem}
                          disabled={!selectedProduct}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                        >
                          <PlusIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Seçili Ürünler Listesi */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Seçili Ürünler</h4>
                    {tempSetItems.length > 0 ? (
                      <div className="space-y-3">
                        {tempSetItems.map((item, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">{item.product_name}</p>
                              <p className="text-sm text-gray-500">
                                {item.quantity} adet × {formatCurrency(item.product_price)} = {formatCurrency(item.quantity * item.product_price)}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeTempSetItem(item.product_id)}
                              className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded-full"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                        
                        {/* Toplam Fiyat */}
                        <div className="mt-4 bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-indigo-900">Toplam Fiyat:</span>
                            <span className="text-lg font-bold text-indigo-900">{formatCurrency(calculateTotalPrice())}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <CubeIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <p className="text-sm text-gray-500 mt-2">Henüz ürün seçilmedi</p>
                        <p className="text-xs text-gray-400 mt-1">Yukarıdan ürün ekleyerek başlayın</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                >
                  {loading ? 'Kaydediliyor...' : (editingSet ? 'Güncelle' : 'Oluştur')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Set Items Modal */}
      {showItemsModal && selectedSet && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                🛋️ {selectedSet.name} - Takım İçeriği
              </h3>
              <button
                onClick={closeItemsModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Add Item */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h4 className="text-sm font-medium text-gray-900 mb-3">➕ Ürün Ekle</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <select
                    value={selectedProduct}
                    onChange={(e) => setSelectedProduct(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Ürün Seçin</option>
                    {products.filter(p => !setItems.find(item => item.product_id === p.id)).map(product => (
                      <option key={product.id} value={product.id}>
                        {product.name} - {formatCurrency(product.price)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min="1"
                    value={selectedQuantity}
                    onChange={(e) => setSelectedQuantity(e.target.value)}
                    placeholder="Adet"
                    className="block w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={addSetItem}
                    disabled={!selectedProduct}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                  >
                    <PlusIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Items List */}
            <div className="space-y-4">
              {setItems.length > 0 ? (
                setItems.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden">
                        {item.products?.image_url ? (
                          <img
                            src={item.products.image_url}
                            alt={item.products.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <CubeIcon className="h-8 w-8 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          {item.products?.name || 'Ürün bulunamadı'}
                        </h4>
                        <p className="text-sm text-gray-500">
                          {item.quantity} adet × {formatCurrency(item.price_per_unit)} = {formatCurrency(item.quantity * item.price_per_unit)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => removeSetItem(item.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-full"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <CubeIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="text-sm text-gray-500 mt-2">Bu takımda henüz ürün bulunmuyor.</p>
                </div>
              )}
            </div>

            <div className="flex justify-end pt-4">
              <button
                onClick={closeItemsModal}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProductSetsPage 